/**
 * Guarantee Engine v3
 * Shared cart logic for both product page and cart page warranty widgets.
 * Handles: mutual exclusivity, cannot-add-alone guard, cart updates, UI sync,
 *          AUTO-REMOVE warranty when last regular product is removed.
 */

(function (w) {
  'use strict';

  if (w.__GE__) return; // singleton guard

  const G30_VID   = 53947930214742;
  const G6M_VID   = 53947936833878;
  const G6M_CENTS = 3000;
  const GUARD_VIDS = new Set([G30_VID, G6M_VID]);

  /* ─── Utility ──────────────────────────────────────────────────── */

  function fmt(cents) {
    if (w.Shopify && typeof w.Shopify.formatMoney === 'function') {
      return w.Shopify.formatMoney(cents, '\u00a3{{amount}}');
    }
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: 'GBP',
    }).format(cents / 100);
  }

  async function apiFetch(url, opts) {
    const res = await fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
        ...(opts && opts.headers),
      },
      ...opts,
    });
    if (!res.ok) throw new Error('[GE] ' + url + ' -> ' + res.status);
    return res.json();
  }

  function getCart() {
    return apiFetch('/cart.js');
  }

  async function addVariant(vid, quantity = 1) {
    const fd = new FormData();
    fd.append('id', vid);
    fd.append('quantity', String(Math.max(1, quantity)));
    fd.append('form_type', 'product');
    return apiFetch('/cart/add.js', { method: 'POST', body: fd });
  }

  async function removeVariantDirect(vid) {
    /* Fetch cart fresh, find item, remove by line index */
    const cart = await getCart();
    const item  = cart.items.find((i) => i.variant_id === vid);
    if (!item) return cart;
    return apiFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: cart.items.indexOf(item) + 1, quantity: 0 }),
    });
  }

  /* Alias used internally & exported */
  const removeVariant = removeVariantDirect;

  async function setVariantQuantityDirect(vid, quantity) {
    const cart = await getCart();
    const item = cart.items.find((i) => i.variant_id === vid);

    if (!item) {
      if (quantity > 0) return addVariant(vid, quantity);
      return cart;
    }

    return apiFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: cart.items.indexOf(item) + 1,
        quantity: Math.max(0, quantity),
      }),
    });
  }

  /* ─── Cart state helpers ────────────────────────────────────────── */

  function warrantyInCart(cart) {
    const g6m = cart.items.some((i) => i.variant_id === G6M_VID);

    return {
      g30: !g6m,
      g6m,
    };
  }

  function hasProducts(cart) {
    return cart.items.some((i) => !GUARD_VIDS.has(i.variant_id));
  }

  function productQuantity(cart) {
    return cart.items.reduce((total, item) => {
      return GUARD_VIDS.has(item.variant_id) ? total : total + item.quantity;
    }, 0);
  }

  function regularProductsWantPaidWarranty(cart) {
    return cart.items.some((item) => {
      if (GUARD_VIDS.has(item.variant_id)) return false;

      const properties = cartLinePropertiesObject(item);
      return properties.Guarantee === '6-Month Extended Guarantee'
        || properties['Guarantee price'] === 'GBP 30 each';
    });
  }

  async function syncWarrantyQuantity(cart) {
    const qty = productQuantity(cart);

    if (!qty) return cleanupOrphanedWarranty();

    let workingCart = cart;
    const g30Item = workingCart.items.find((i) => i.variant_id === G30_VID);

    if (g30Item) {
      workingCart = await removeVariantDirect(G30_VID);
    }

    const g6mItem = workingCart.items.find((i) => i.variant_id === G6M_VID);
    const wantsPaidWarranty = regularProductsWantPaidWarranty(workingCart);

    if (wantsPaidWarranty && !g6mItem) {
      await addVariant(G6M_VID, qty);
      return getCart();
    }

    if (wantsPaidWarranty && g6mItem && g6mItem.quantity !== qty) {
      return setVariantQuantityDirect(G6M_VID, qty);
    }

    if (!wantsPaidWarranty && g6mItem) {
      await removeVariantDirect(G6M_VID);
      return getCart();
    }

    return workingCart;
  }

  function cartLinePropertiesObject(item) {
    const properties = item.properties || {};

    if (Array.isArray(properties)) {
      return properties.reduce((result, property) => {
        if (property && property.name) result[property.name] = property.value;
        if (property && property.first) result[property.first] = property.last;
        return result;
      }, {});
    }

    return { ...properties };
  }

  async function updateRegularProductGuaranteeProperties(guarantee, guaranteePrice) {
    let cart = await getCart();
    let guard = 0;

    while (guard < 20) {
      const lineIndex = cart.items.findIndex((item) => {
        if (GUARD_VIDS.has(item.variant_id)) return false;
        const properties = cartLinePropertiesObject(item);
        return properties.Guarantee !== guarantee || properties['Guarantee price'] !== guaranteePrice;
      });

      if (lineIndex < 0) return cart;

      const item = cart.items[lineIndex];
      const properties = cartLinePropertiesObject(item);
      properties.Guarantee = guarantee;
      properties['Guarantee price'] = guaranteePrice;

      cart = await apiFetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex + 1,
          quantity: item.quantity,
          properties,
        }),
      });

      guard += 1;
    }

    return cart;
  }

  /* ─── AUTO-REMOVE orphaned warranties ──────────────────────────── */
  /**
   * Call this after ANY cart change (remove / quantity = 0).
   * If no regular products remain, silently removes all warranties.
   * Returns the final cart state.
   */
  async function cleanupOrphanedWarranty() {
    const cart  = await getCart();
    const noProducts = !hasProducts(cart);

    if (!noProducts) return cart; // nothing to do

    if (cart.items.some((i) => i.variant_id === G30_VID)) await removeVariantDirect(G30_VID);
    if (cart.items.some((i) => i.variant_id === G6M_VID)) await removeVariantDirect(G6M_VID);

    const fresh = await getCart();
    syncAllWidgets(fresh);   // hides widget, resets UI
    announceCartUpdate(fresh);
    return fresh;
  }

  /* ─── Global UI sync ────────────────────────────────────────────── */

  function syncAllWidgets(cart) {
    const state      = warrantyInCart(cart);
    const hasProds   = hasProducts(cart);
    const itemCount  = productQuantity(cart);

    /* Cart bubble */
    document.querySelectorAll(
      '[data-testid="cart-bubble"], .cart-bubble__text-count'
    ).forEach((el) => { el.textContent = itemCount; });

    /* Cart subtotal / total */
    const totalFmt = fmt(cart.total_price);
    document.querySelectorAll('[data-cart-subtotal], [data-cart-total]')
      .forEach((el) => { el.textContent = totalFmt; });

    document.querySelectorAll('[data-guarantee-subtotal-note]')
      .forEach((el) => { el.hidden = !state.g6m; });

    /* Notify each registered widget */
    document.querySelectorAll('[data-ge-widget]').forEach((wrap) => {
      const type = wrap.dataset.geWidget;
      if (type === 'cart') {
        /* Show/hide based on whether real products exist */
        wrap.style.display = hasProds ? '' : 'none';
        if (hasProds) syncCartWidget(wrap, state, cart);
      }
      if (type === 'product') syncProductWidget(wrap, state);
    });
  }

  function getCartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((item) => item.dataset && item.dataset.sectionId)
      .filter(Boolean)
      .filter((id, index, ids) => ids.indexOf(id) === index);
  }

  async function getCartSections() {
    const sectionIds = getCartSectionIds();
    if (!sectionIds.length) return {};

    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionIds.join(','));
    url.searchParams.set('sections_url', window.location.pathname);

    const res = await fetch(url.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
    });

    if (!res.ok) throw new Error('[GE] cart sections refresh -> ' + res.status);
    return res.json();
  }

  function announceCartUpdate(cart) {
    document.dispatchEvent(new CustomEvent('ge:cart:updated', {
      detail: { cart },
    }));

    getCartSections()
      .then((sections) => {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'guarantee-engine',
            data: {
              itemCount: productQuantity(cart),
              source: 'guarantee-engine',
              sections,
            },
          },
        }));
      })
      .catch((error) => {
        console.warn('[GE] cart drawer refresh error', error);
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'guarantee-engine',
            data: {
              itemCount: productQuantity(cart),
              source: 'guarantee-engine',
              sections: {},
            },
          },
        }));
      });
  }
  /* ─── Cart page widget sync ─────────────────────────────────────── */

  function syncCartWidget(wrap, state, cart) {
    const g6mBtn = wrap.querySelector('[data-ge-vid="' + G6M_VID + '"]');
    const includedStatus = wrap.querySelector('.gu-status');

    if (includedStatus) includedStatus.classList.toggle('gu-status--active', state.g30);
    if (g6mBtn) applyCartBtnState(g6mBtn, state.g6m, true);

    if (cart) {
      [[G6M_VID, g6mBtn]].forEach(([vid, btn]) => {
        if (!btn) return;
        const item = cart.items.find((i) => i.variant_id === vid);
        btn.dataset.geInCart = item ? 'true' : 'false';
      });
    }
  }

  function applyCartBtnState(btn, inCart, isPaid) {
    btn.classList.toggle('gu-btn--added', inCart);
    if (inCart) {
      btn.innerHTML =
        '<svg viewBox="0 0 16 16" fill="none"><path d="M3 8l3.5 3.5L13 5"' +
        ' stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        (isPaid ? 'Remove' : 'Included');
    } else {
      btn.textContent = isPaid ? 'Add - \u00a330 each' : 'Use Free';
    }
  }

  /* ─── Product page widget sync ──────────────────────────────────── */

  function syncProductWidget(wrap, state) {
    const g30Label = wrap.querySelector('[data-ge-option="30d"]');
    const g6mLabel = wrap.querySelector('[data-ge-option="6m"]');
    const g30Cb    = wrap.querySelector('[data-ge-check="' + G30_VID + '"]');
    const g6mCb    = wrap.querySelector('[data-ge-check="' + G6M_VID + '"]');
    const g30Box   = g30Label && g30Label.querySelector('.gpu-checkbox');
    const g6mBox   = g6mLabel && g6mLabel.querySelector('.gpu-checkbox');

    const g30Checked = state.g30 || (!state.g30 && !state.g6m);
    if (g30Cb)    g30Cb.checked = g30Checked;
    if (g6mCb)    g6mCb.checked = state.g6m;
    if (g30Box)   g30Box.classList.toggle('gpu-checkbox--checked', g30Checked);
    if (g6mBox)   g6mBox.classList.toggle('gpu-checkbox--checked', state.g6m);
    if (g30Label) g30Label.classList.toggle('gpu-option--checked', g30Checked);
    if (g6mLabel) g6mLabel.classList.toggle('gpu-option--checked', state.g6m);
  }

  /* ─── Core toggle logic ─────────────────────────────────────────── */

  let _interceptorBusy = false;

  async function toggleWarranty(vid, setLoading) {
  setLoading(true);
  _interceptorBusy = true;
  try {
    const cart = await getCart();

    if (!hasProducts(cart)) {
      showToast('Please add a product to your cart first.');
      return;
    }

    const state = warrantyInCart(cart);
    const inCart = vid === G6M_VID && state.g6m;

    if (vid === G30_VID) {
      await removeVariant(G6M_VID);
      await removeVariant(G30_VID);
    } else if (inCart) {
      await removeVariant(vid);
    } else {
      await removeVariant(G30_VID);
      await addVariant(vid, productQuantity(cart));
    }

    await updateRegularProductGuaranteeProperties(
      vid === G6M_VID && !inCart ? '6-Month Extended Guarantee' : '30-Day Guarantee Included',
      vid === G6M_VID && !inCart ? 'GBP 30 each' : 'Free'
    );

    const fresh = await getCart();
    _interceptorBusy = false;
    syncAllWidgets(fresh);
    announceCartUpdate(fresh);
  } catch (e) {
    console.error(e);
  } finally {
    _interceptorBusy = false;
    setLoading(false);
  }
}

  /* ─── Toast notification ────────────────────────────────────────── */

  function showToast(msg) {
    let toast = document.getElementById('ge-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'ge-toast';
      toast.style.cssText = [
        'position:fixed','bottom:24px','left:50%',
        'transform:translateX(-50%)','background:#1b4d36',
        'color:#d0f0e2','padding:10px 20px','border-radius:8px',
        'font-size:13px','font-weight:600','z-index:99999',
        'pointer-events:none','opacity:0','transition:opacity .25s',
        'white-space:nowrap',
      ].join(';');
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.style.opacity = '1';
    clearTimeout(toast._t);
    toast._t = setTimeout(() => { toast.style.opacity = '0'; }, 3000);
  }

  /* ─── Cart change interceptor ───────────────────────────────────── */
  /**
   * Intercepts ALL /cart/change.js calls (quantity updates, removes).
   * After each one, checks if any regular products still exist.
   * If not → auto-removes orphaned warranties.
   */
  (function installChangeInterceptor() {
    const _fetch = w.fetch;
    let _busy    = false;

    w.fetch = function (input, init) {
      const url      = typeof input === 'string' ? input : (input && input.url) || '';
      const isChange = url.includes('/cart/change');
      const promise  = _fetch.apply(this, arguments);

      if (isChange && !_busy && !_interceptorBusy) {
        promise.then(async (res) => {
          if (!res.ok) return;
          try {
            /* The response from /cart/change.js IS the full cart */
            const cart = await res.clone().json();

            if (!hasProducts(cart)) {
              _busy = true;
              /* Remove any warranties silently */
              if (cart.items.some((i) => i.variant_id === G30_VID)) await removeVariantDirect(G30_VID);
              if (cart.items.some((i) => i.variant_id === G6M_VID)) await removeVariantDirect(G6M_VID);
              const fresh = await getCart();
              syncAllWidgets(fresh);
              announceCartUpdate(fresh);
              _busy = false;
            } else {
              _busy = true;
              await syncWarrantyQuantity(cart);
              const fresh = await getCart();
              syncAllWidgets(fresh);
              announceCartUpdate(fresh);
              _busy = false;
            }
          } catch (e) {
            console.warn('[GE] change interceptor error', e);
            _busy = false;
          }
        });
      }

      return promise;
    };
  })();

  /* ─── Product page: add-to-cart interceptor ─────────────────────── */
  let _productInterceptInstalled = false;

  function installProductInterceptor(wrap) {
    if (_productInterceptInstalled) return;
    _productInterceptInstalled = true;

    /* NOTE: fetch is already wrapped above for /cart/change.
       We wrap again here only for /cart/add — the change wrapper
       is already in place and won't double-wrap because of the url check. */
    const _fetch = w.fetch;
    let _busy    = false;

    w.fetch = function (input, init) {
      const url   = typeof input === 'string' ? input : (input && input.url) || '';
      const isAdd = url.includes('/cart/add');
      const promise = _fetch.apply(this, arguments);

      if (isAdd && !_busy) {
        promise.then(async (res) => {
          if (!res.ok) return;
          try {
            const data  = await res.clone().json();
            const items = Array.isArray(data.items) ? data.items : [data];
            if (items.every((i) => GUARD_VIDS.has(i.variant_id))) return;

            const selected = [...wrap.querySelectorAll('[data-ge-check]:checked')]
              .map((cb) => parseInt(cb.dataset.geCheck, 10))
              .filter(Boolean);

            _busy = true;
            _interceptorBusy = true;
            const cart  = await getCart();
            const state = warrantyInCart(cart);
            const wantsPaidWarranty = selected.includes(G6M_VID);

            await removeVariantDirect(G30_VID);

            const toRemove = [G30_VID, G6M_VID].filter(
              (v) => v === G6M_VID && !wantsPaidWarranty && state.g6m
            );
            for (const v of toRemove) await removeVariantDirect(v);

            if (wantsPaidWarranty) {
              const vid = G6M_VID;
              const c  = await getCart();
              const ws = warrantyInCart(c);
              const already = ws.g6m;
              if (!already) await addVariant(vid, productQuantity(c));
              if (already) await setVariantQuantityDirect(vid, productQuantity(c));
            }

            await updateRegularProductGuaranteeProperties(
              wantsPaidWarranty ? '6-Month Extended Guarantee' : '30-Day Guarantee Included',
              wantsPaidWarranty ? 'GBP 30 each' : 'Free'
            );

            const fresh = await getCart();
            syncAllWidgets(fresh);
            announceCartUpdate(fresh);
          } catch (e) {
            console.warn('[GE] add interceptor error', e);
          } finally {
            _busy = false;
            _interceptorBusy = false;
          }
        });
      }

      return promise;
    };
  }

  /* ─── Public API ────────────────────────────────────────────────── */

  w.__GE__ = {
    G30_VID, G6M_VID, G6M_CENTS,
    fmt,
    getCart,
    addVariant,
    removeVariant,
    toggleWarranty,
    syncAllWidgets,
    announceCartUpdate,
    syncCartWidget,
    syncProductWidget,
    warrantyInCart,
    hasProducts,
    productQuantity,
    regularProductsWantPaidWarranty,
    syncWarrantyQuantity,
    updateRegularProductGuaranteeProperties,
    cleanupOrphanedWarranty,
    installProductInterceptor,
    showToast,
  };

})(window);
